import { Outlet, NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  ShieldCheck,
  Users,
  CalendarDays,
  UserCircle2,
  MailPlus,
  UserPlus,
  FileCode2,
  FileText,
  Calculator,
  CalendarClock,
  FileCheck2,
  Star,
  Megaphone,
  Sparkles,
  Menu,
  X,
  Globe,
  LifeBuoy,
  PartyPopper,
  Mail,
} from 'lucide-react'
import type { NavSection } from '@/components/layout/MobileNav'
import { useUIStore } from '@/store/uiStore'
import { UserMenu } from '@/components/shared/UserMenu'
import { ROUTES } from '@/constants/routes'
import { webUrl } from '@/constants/site'
import { cn } from '@/lib/utils'
import { useUnreadPeerMessages } from '@/hooks/useUnreadMessages'

/**
 * Voci raggruppate per tipo di lavoro: con quattordici link l'elenco piatto era
 * diventato illeggibile e sui monitor bassi finiva sotto il bordo. Stessa forma
 * della sidebar operatore (`NavSection`), così i due pannelli si comportano
 * allo stesso modo anche da collassati.
 */
const baseSections: NavSection[] = [
  {
    items: [
      { label: 'Dashboard',            icon: LayoutDashboard, to: ROUTES.ADMIN.DASHBOARD },
    ],
  },
  {
    label: 'Da moderare',
    items: [
      { label: 'Revisioni',            icon: ShieldCheck,     to: ROUTES.ADMIN.REVIEWS },
      { label: 'Articoli da rivedere', icon: FileCheck2,      to: ROUTES.ADMIN.ARTICLES_REVIEW },
      { label: 'Recensioni',           icon: Star,            to: ROUTES.ADMIN.REVIEWS_MODERATION },
    ],
  },
  {
    label: 'Persone',
    items: [
      { label: 'Operatori',            icon: Users,           to: ROUTES.ADMIN.OPERATORS },
      { label: 'Clienti',              icon: UserCircle2,     to: ROUTES.ADMIN.USERS },
      { label: 'Lead',                 icon: UserPlus,        to: ROUTES.ADMIN.LEADS },
      { label: 'Eventi',               icon: PartyPopper,     to: ROUTES.ADMIN.EVENTS },
      { label: 'Inviti',               icon: MailPlus,        to: ROUTES.ADMIN.INVITES },
      { label: 'Supporto operatori',   icon: LifeBuoy,        to: ROUTES.ADMIN.SUPPORT },
    ],
  },
  {
    label: 'Contenuti',
    items: [
      { label: 'Articoli',             icon: FileText,        to: ROUTES.ADMIN.ARTICLES },
      { label: 'Annunci',              icon: Megaphone,       to: ROUTES.ADMIN.ANNOUNCEMENTS },
      { label: 'Template',             icon: FileCode2,       to: ROUTES.ADMIN.TEMPLATES },
    ],
  },
  {
    label: 'Attività',
    items: [
      { label: 'Appuntamenti',         icon: CalendarDays,    to: ROUTES.ADMIN.APPOINTMENTS },
      { label: 'Quiz matching',        icon: Sparkles,        to: ROUTES.ADMIN.MATCHING },
      { label: 'Contabilità',          icon: Calculator,      to: ROUTES.ADMIN.ACCOUNTING },
      { label: 'Automazioni',          icon: CalendarClock,   to: ROUTES.ADMIN.AUTOMATIONS },
      { label: 'Email inviate',        icon: Mail,            to: ROUTES.ADMIN.EMAIL_LOG },
    ],
  },
]

export function AdminLayout() {
  const { sidebarOpen, toggleSidebar } = useUIStore()
  // Casella condivisa «Supporto OLLYlife»: non letti comuni a tutti gli admin.
  const unreadSupport = useUnreadPeerMessages()
  const navSections: NavSection[] = baseSections.map((section) => ({
    ...section,
    items: section.items.map((item) =>
      item.to === ROUTES.ADMIN.SUPPORT ? { ...item, badge: unreadSupport } : item,
    ),
  }))

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">

      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside
        className={cn(
          'flex flex-col overflow-hidden bg-holly-900 text-holly-100 transition-all duration-300',
          sidebarOpen ? 'w-60' : 'w-16'
        )}
      >
        {/* Logo + toggle */}
        <div className="flex h-16 items-center justify-between border-b border-holly-800 px-4">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-holly-500 text-white font-bold text-xs">
                H
              </div>
              <span className="font-semibold text-white">Admin Panel</span>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className="rounded-lg p-1.5 text-holly-300 hover:bg-holly-800 hover:text-white"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Link al sito pubblico */}
        <a
          href={webUrl('/')}
          title="Vai al sito pubblico"
          className={cn(
            'mx-2 mt-2 flex items-center gap-2 rounded-lg border border-holly-800 px-3 py-2 text-xs font-medium text-holly-300 hover:border-holly-600 hover:text-white',
            !sidebarOpen && 'justify-center px-2'
          )}
        >
          <Globe className="h-4 w-4 flex-shrink-0" />
          {sidebarOpen && <span>Vai al sito</span>}
        </a>

        {/* Nav links — scrollabile su viewport bassi.
            `min-h-0` è indispensabile: senza, il flex item non scende sotto
            l'altezza del contenuto e la lista sborda oltre il menu utente. */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2 py-3">
          {navSections.map((section, si) => (
            <div key={section.label ?? `section-${si}`}>
              {si > 0 && (
                sidebarOpen && section.label ? (
                  <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-holly-400">
                    {section.label}
                  </div>
                ) : (
                  <div className="mx-3 my-2 border-t border-holly-800" />
                )
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    title={!sidebarOpen ? item.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-holly-700 text-white'
                          : 'text-holly-300 hover:bg-holly-800 hover:text-white',
                        !sidebarOpen && 'justify-center'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && <span>{item.label}</span>}
                    {item.badge && item.badge > 0 ? (
                      <span className={cn(
                        'ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-orange-500 px-1.5 text-xs font-bold text-white',
                        !sidebarOpen && 'absolute right-1 top-1 ml-0'
                      )}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    ) : null}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User menu with dark override */}
        <div className={cn('border-t border-holly-800 p-3', !sidebarOpen && 'flex justify-center')}>
          <div className="[&_button]:text-holly-200 [&_button:hover]:bg-holly-800 [&_p]:text-holly-300">
            <UserMenu compact={!sidebarOpen} />
          </div>
        </div>
      </aside>

      {/* ── Main content ────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto px-8 py-8">
        <Outlet />
      </main>
    </div>
  )
}
